import React, { useState } from 'react';
import { contactData } from '../data/mock';
import { MapPin, Mail, Phone, Loader2 } from 'lucide-react';

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    projectType: '',
    message: ''
  });

  const [submitStatus, setSubmitStatus] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setErrorMessage('');
    
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/contact`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });
      
      if (!response.ok) {
        throw new Error('Failed to submit enquiry');
      }
      
      const data = await response.json();
      console.log('Enquiry submitted:', data);
      setSubmitStatus('success');
      
      // Reset form after success
      setTimeout(() => {
        setFormData({
          name: '',
          email: '',
          phone: '',
          projectType: '',
          message: ''
        });
        setSubmitStatus('');
      }, 5000);
      
    } catch (error) {
      console.error('Error submitting enquiry:', error);
      setSubmitStatus('error');
      setErrorMessage('Failed to submit enquiry. Please try again or contact us directly.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const projectTypes = [
    'Primary Health Centre',
    'Community Health Centre',
    'District Hospital',
    'Medical College & Hospital',
    'Super-Specialty Hospital',
    'Cancer Institute',
    'PG Institute',
    'Special Category Hospital',
    'Government Healthcare Institution',
    'Research & Referral Institute',
    'Other'
  ];

  return (
    <div>
      {/* Hero Section */}
      <section className="section-padding" style={{ background: 'var(--bg-primary)' }}>
        <div className="container text-center">
          <h1 className="hero-large mb-6">{contactData.title}</h1>
          <p className="body-large" style={{ color: 'var(--text-secondary)', maxWidth: '800px', margin: '0 auto' }}>
            {contactData.description}
          </p>
        </div>
      </section>

      {/* Contact Form & Info */}
      <section className="section-padding" style={{ background: 'var(--bg-secondary)' }}>
        <div className="container">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Contact Form */}
            <div 
              style={{
                background: 'var(--bg-primary)',
                padding: '48px',
                borderRadius: '0px',
                border: '1px solid var(--border-light)'
              }}
            >
              <h2 className="heading-1 mb-6">Request Project Consultation</h2>
              
              {submitStatus === 'success' && (
                <div 
                  style={{
                    background: 'rgba(0, 128, 128, 0.1)',
                    padding: '16px',
                    marginBottom: '24px',
                    border: '1px solid var(--brand-teal)',
                    borderRadius: '4px'
                  }}
                >
                  <p className="body-regular" style={{ color: 'var(--brand-teal)' }}>
                    ✓ Thank you for your inquiry! Your enquiry has been sent to our team at info@pinnaclebiomed.com. We will contact you shortly.
                  </p>
                </div>
              )}

              {submitStatus === 'error' && (
                <div 
                  style={{
                    background: 'rgba(204, 0, 0, 0.1)',
                    padding: '16px',
                    marginBottom: '24px',
                    border: '1px solid var(--brand-red)',
                    borderRadius: '4px'
                  }}
                >
                  <p className="body-regular" style={{ color: 'var(--brand-red)' }}>
                    {errorMessage}
                  </p>
                </div>
              )}

              <form onSubmit={handleSubmit}>
                <div className="space-y-4">
                  <div>
                    <label htmlFor="name" className="body-regular block mb-2" style={{ color: 'var(--text-primary)' }}>
                      Full Name *
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      required
                      value={formData.name}
                      onChange={handleChange}
                      style={{
                        width: '100%',
                        padding: '16px',
                        border: '1px solid var(--border-medium)',
                        borderRadius: '0px',
                        fontSize: '14px',
                        fontFamily: "'Suisse Regular', sans-serif",
                        background: 'var(--bg-primary)',
                        color: 'var(--text-primary)'
                      }}
                    />
                  </div>

                  <div>
                    <label htmlFor="email" className="body-regular block mb-2" style={{ color: 'var(--text-primary)' }}>
                      Email Address *
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      required
                      value={formData.email}
                      onChange={handleChange}
                      style={{
                        width: '100%',
                        padding: '16px',
                        border: '1px solid var(--border-medium)',
                        borderRadius: '0px',
                        fontSize: '14px',
                        fontFamily: "'Suisse Regular', sans-serif",
                        background: 'var(--bg-primary)',
                        color: 'var(--text-primary)'
                      }}
                    />
                  </div>

                  <div>
                    <label htmlFor="phone" className="body-regular block mb-2" style={{ color: 'var(--text-primary)' }}>
                      Phone Number *
                    </label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      required
                      value={formData.phone}
                      onChange={handleChange}
                      style={{
                        width: '100%',
                        padding: '16px',
                        border: '1px solid var(--border-medium)',
                        borderRadius: '0px',
                        fontSize: '14px',
                        fontFamily: "'Suisse Regular', sans-serif",
                        background: 'var(--bg-primary)',
                        color: 'var(--text-primary)'
                      }}
                    />
                  </div>

                  <div>
                    <label htmlFor="projectType" className="body-regular block mb-2" style={{ color: 'var(--text-primary)' }}>
                      Project Type *
                    </label>
                    <select
                      id="projectType"
                      name="projectType"
                      required
                      value={formData.projectType}
                      onChange={handleChange}
                      style={{
                        width: '100%',
                        padding: '16px',
                        border: '1px solid var(--border-medium)',
                        borderRadius: '0px',
                        fontSize: '14px',
                        fontFamily: "'Suisse Regular', sans-serif",
                        background: 'var(--bg-primary)',
                        color: 'var(--text-primary)'
                      }}
                    >
                      <option value="">Select Project Type</option>
                      {projectTypes.map((type) => (
                        <option key={type} value={type}>{type}</option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label htmlFor="message" className="body-regular block mb-2" style={{ color: 'var(--text-primary)' }}>
                      Project Details *
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      required
                      rows="5"
                      value={formData.message}
                      onChange={handleChange}
                      placeholder="Please share details about your project requirements, location, capacity, and timeline..."
                      style={{
                        width: '100%',
                        padding: '16px',
                        border: '1px solid var(--border-medium)',
                        borderRadius: '0px',
                        fontSize: '14px',
                        fontFamily: "'Suisse Regular', sans-serif",
                        background: 'var(--bg-primary)',
                        color: 'var(--text-primary)',
                        resize: 'vertical'
                      }}
                    ></textarea>
                  </div>

                  <button 
                    type="submit" 
                    className="btn-primary w-full"
                    disabled={isSubmitting}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '8px',
                      opacity: isSubmitting ? 0.7 : 1
                    }}
                  >
                    {isSubmitting ? (
                      <>
                        <Loader2 size={18} className="animate-spin" />
                        Submitting...
                      </>
                    ) : (
                      'Submit Inquiry'
                    )}
                  </button>
                </div>
              </form>
            </div>

            {/* Contact Information */}
            <div>
              <h2 className="heading-1 mb-6">Contact Information</h2>
              
              <div className="space-y-6 mb-12">
                <div className="flex items-start gap-4">
                  <Mail 
                    size={24} 
                    style={{ 
                      color: 'var(--interactive-base)',
                      flexShrink: 0,
                      marginTop: '4px'
                    }} 
                  />
                  <div>
                    <h3 className="body-large mb-2" style={{ fontWeight: '700', fontFamily: "'Suisse Medium', sans-serif" }}>
                      Email
                    </h3>
                    <p className="body-regular" style={{ color: 'var(--text-secondary)' }}>
                      {contactData.email}
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <Phone 
                    size={24} 
                    style={{ 
                      color: 'var(--interactive-base)',
                      flexShrink: 0,
                      marginTop: '4px'
                    }} 
                  />
                  <div>
                    <h3 className="body-large mb-2" style={{ fontWeight: '700', fontFamily: "'Suisse Medium', sans-serif" }}>
                      Phone
                    </h3>
                    <p className="body-regular" style={{ color: 'var(--text-secondary)' }}>
                      {contactData.phone}
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <MapPin 
                    size={24} 
                    style={{ 
                      color: 'var(--interactive-base)',
                      flexShrink: 0,
                      marginTop: '4px'
                    }} 
                  />
                  <div>
                    <h3 className="body-large mb-2" style={{ fontWeight: '700', fontFamily: "'Suisse Medium', sans-serif" }}>
                      Location
                    </h3>
                    <p className="body-regular" style={{ color: 'var(--text-secondary)' }}>
                      {contactData.address}
                    </p>
                  </div>
                </div>
              </div>

              <div 
                style={{
                  background: 'var(--bg-primary)',
                  padding: '40px',
                  borderRadius: '0px',
                  border: '1px solid var(--border-light)'
                }}
              >
                <h3 className="heading-2 mb-4">Why Choose Pinnacle Biomed?</h3>
                <ul className="space-y-3">
                  <li className="body-regular" style={{ color: 'var(--text-secondary)' }}>
                    • Single-agency accountability for complete project lifecycle
                  </li>
                  <li className="body-regular" style={{ color: 'var(--text-secondary)' }}>
                    • Decades of proven experience across all healthcare facility levels
                  </li>
                  <li className="body-regular" style={{ color: 'var(--text-secondary)' }}>
                    • Full compliance with national and international standards
                  </li>
                  <li className="body-regular" style={{ color: 'var(--text-secondary)' }}>
                    • End-to-end operations with complete staffing solutions
                  </li>
                  <li className="body-regular" style={{ color: 'var(--text-secondary)' }}>
                    • Capacity from PHC to 3600-bed PG institutes
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Google Map Section */}
      <section className="section-padding" style={{ background: 'var(--bg-secondary)' }}>
        <div className="container">
          <div className="text-center mb-8">
            <h2 className="hero-medium mb-4">
              <span style={{ color: 'var(--brand-teal)' }}>Corporate</span>
              {' '}
              <span style={{ color: 'var(--brand-orange)' }}>Office Location</span>
            </h2>
            <p className="body-large" style={{ color: 'var(--text-secondary)', maxWidth: '700px', margin: '0 auto' }}>
              Visit us at our corporate office in Parel, Mumbai
            </p>
          </div>
          
          <div 
            style={{
              background: 'var(--bg-primary)',
              borderRadius: '12px',
              overflow: 'hidden',
              border: '1px solid var(--border-light)',
              boxShadow: '0 4px 24px rgba(0, 0, 0, 0.1)'
            }}
          >
            {/* Map */}
            <div style={{ width: '100%', height: '400px' }}>
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3772.5441761766087!2d72.83901231490055!3d19.00089078714246!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3be7cf26f4e0a0a1%3A0x7c5d3b6c6a6b8c0d!2sTrust%20House%2C%20Dr%20E%20Borges%20Rd%2C%20Parel%2C%20Mumbai%2C%20Maharashtra%20400012!5e0!3m2!1sen!2sin!4v1640000000000!5m2!1sen!2sin"
                width="100%"
                height="100%"
                style={{ border: 0 }}
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="Pinnacle Biomed Office Location"
              ></iframe>
            </div>
            
            {/* Address Bar */}
            <div 
              style={{
                padding: '24px 32px',
                background: 'linear-gradient(135deg, var(--brand-teal) 0%, #006666 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                flexWrap: 'wrap',
                gap: '20px'
              }}
            >
              <div className="flex items-center gap-4">
                <div 
                  style={{
                    width: '50px',
                    height: '50px',
                    borderRadius: '50%',
                    background: 'rgba(255, 255, 255, 0.2)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                >
                  <MapPin size={24} style={{ color: 'white' }} />
                </div>
                <div>
                  <h3 className="heading-3" style={{ color: 'white', marginBottom: '4px' }}>
                    Pinnacle Biomed Pvt. Ltd.
                  </h3>
                  <p className="body-regular" style={{ color: 'rgba(255, 255, 255, 0.9)' }}>
                    3A & 3B, 3rd Floor, Trust House, 35 Hospital Avenue, Dr. E. Borges Road, Parel, Mumbai - 400012
                  </p>
                </div>
              </div>
              <a 
                href="https://www.google.com/maps/search/Trust+House+Dr+E+Borges+Road+Parel+Mumbai+400012"
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  padding: '12px 24px',
                  background: 'var(--brand-orange)',
                  color: 'white',
                  borderRadius: '8px',
                  textDecoration: 'none',
                  fontFamily: "'Suisse Medium', sans-serif",
                  fontSize: '14px',
                  transition: 'all 0.3s ease'
                }}
                className="get-directions-btn"
              >
                Get Directions →
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Pan India Section */}
      <section className="section-padding" style={{ background: 'var(--bg-primary)' }}>
        <div className="container text-center">
          <h2 className="hero-medium mb-4">Pan India Project Execution</h2>
          <p className="body-large" style={{ color: 'var(--text-secondary)', maxWidth: '700px', margin: '0 auto' }}>
            Pinnacle Biomed India Pvt. Ltd. delivers turnkey healthcare infrastructure solutions across Government, Private, and Trust Hospitals throughout India. From primary health centres to super-specialty hospitals, we bring unmatched expertise and accountability to every project.
          </p>
        </div>
      </section>
    </div>
  );
};

export default Contact;
